import { connect } from 'react-redux';
import React from 'react';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import HelpIcon from '@mui/icons-material/HelpRounded';
import { Grid, Paper, Tooltip, IconButton, Menu, ListItem, ListItemText } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { detectLocalPausePlayPreviewPress } from '../../../../utilities/eventUtilities';
import CreatePageConstants from '../CreatePageConstants';
import AudioPlaybackPanel from '../components/PlaybackControls/AudioPlaybackPanel';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';
import { createActions } from '../store';
import FreezeVideoBackgroundSelect from './controls/FreezeVideoBackgroundSelect';
import PreviewQualitySelect from './controls/PreviewQualitySelect';
import TooltipHelpButton from '../../../buttons/TooltipHelpButton';
import AudioAnalysisOptionSelect from './controls/AudioAnalysisOptionSelect';
import EditHistoryOptionSelect from './controls/EditHistoryOptionSelect';
import AssetUploadingModeSelect from './controls/AssetUploadingModeSelect';
import OptimizationModeRecommendationCard from './controls/OptimizationModeRecommendationCard';
import FullscreenPreviewButton from './controls/FullscreenPreviewButton';

const styles = theme => ({
    bottomBarContainer: {
        height: theme.spacing(8),
        bottom: 0,
        left: 0,
        right: 0,
        position: 'relative',
        borderTop: theme.palette.mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)',
        padding: '0 ' + theme.spacing(2).toString(),
    },
    buttonContainerGrid: {
        height: '100%',
        marginTop: 0,
        flexWrap: 'nowrap'
    },
    audioPlaybackWrapper: {
        flexGrow: 1
    },
    fullscreenButtonWrapper: {
        width: theme.spacing(6)
    },
    previewSettingsWrapper: {
        width: theme.spacing(6)
    },
    optimizationModeWrapper: {
        width: theme.spacing(6)
    },
    helpButtonWrapper: {
        width: theme.spacing(6)
    }
});

class BottomBarActions extends React.Component {
    state = {
        previewSettingsMenuAnchorEl: null
    }

    render() {
        const { classes, optimizationModeEnabled } = this.props;
        const { previewSettingsMenuAnchorEl } = this.state;

        return (
            <Paper 
                className={classes.bottomBarContainer} 
                square 
                elevation={0}
            >
                <Grid 
                    container
                    direction='row'
                    alignItems='center'
                    className={classes.buttonContainerGrid}
                >
                    <Grid 
                        className={classes.audioPlaybackWrapper} 
                        id='playback-controls-container'
                    >
                        {/* Render the audio controls and timeline */}
                        <AudioPlaybackPanel />
                    </Grid>
                    <Grid className={classes.optimizationModeWrapper}>
                        {/* The toggle button plus the recommendation card anchored above it */}
                        <OptimizationModeRecommendationCard />
                    </Grid>
                    <Grid className={classes.fullscreenButtonWrapper}>
                        <FullscreenPreviewButton />
                    </Grid>
                    <Grid className={classes.previewSettingsWrapper}>
                        <Tooltip
                            title={optimizationModeEnabled
                                ? 'Preview Settings are managed by Optimization Mode. Turn it off to change them.'
                                : 'Preview Settings'}
                            placement={'top'}
                            arrow
                        >
                            <div>
                                <IconButton
                                    onClick={(event) => this.setState({previewSettingsMenuAnchorEl: event.currentTarget})}
                                    disabled={optimizationModeEnabled}
                                    aria-label='Preview Settings'
                                    aria-controls='preview-settings-menu'
                                    aria-haspopup='true'
                                    size="large">
                                    <SettingsIcon />
                                </IconButton>
                            </div>
                        </Tooltip>
                    </Grid>
                    <Grid className={classes.helpButtonWrapper}>
                        <Tooltip
                            title={'Help'}
                            placement={'top'}
                            arrow
                        >
                            <IconButton onClick={this.onHelpButtonClick} aria-label='Help' size="large">
                                <HelpIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Menu
                    id='preview-settings-menu'
                    anchorEl={previewSettingsMenuAnchorEl}
                    keepMounted
                    open={Boolean(previewSettingsMenuAnchorEl) && !optimizationModeEnabled}
                    onClose={() => this.setState({previewSettingsMenuAnchorEl: null})}
                >
                    <ListItem>
                        <ListItemText>
                            Video Backgrounds
                            <TooltipHelpButton
                                title='While paused, video backgrounds will not animate while playing the editor preview. 
                                This can improve editor performance. 
                                Video backgrounds will still be animated in final exports.'
                            />
                        </ListItemText>
                    </ListItem>
                    <FreezeVideoBackgroundSelect />

                    <ListItem>
                        <ListItemText>
                            Preview Quality
                            <TooltipHelpButton
                                title='Picking a lower quality can improve the performance of the editor. 
                                This setting does not affect export quality.'
                            />
                        </ListItemText>
                    </ListItem>
                    <PreviewQualitySelect />

                    <ListItem>
                        <ListItemText>
                            Audio Analysis
                            <TooltipHelpButton
                                title='Disable to skip audio analysis for preview playback.
                                When disabled, the preview will not synchronize to your music.
                                However, exported videos will still be synchronized.
                                Consider disabling audio analysis if you experience issues while uploading your audio files.'
                            />
                        </ListItemText>
                    </ListItem>
                    <AudioAnalysisOptionSelect />

                    <ListItem>
                        <ListItemText>
                            Edit History
                            <TooltipHelpButton
                                title='Edit history allows you to undo/redo edits.
                                Disabling edit history can improve editor performance.'
                            />
                        </ListItemText>
                    </ListItem>
                    <EditHistoryOptionSelect />

                    <ListItem>
                        <ListItemText>
                            Asset Uploading
                            <TooltipHelpButton
                                title='When submitting audio or visual assets, lazy upload will render them in the editor from your file system, and upload them when you save the video.
                                This allows you to preview your assets in the editor quicker, but can consume additional memory.
                                If you experience performance problems you can enable direct upload so that assets are immediately uploaded to storage when submitted.'
                            />
                        </ListItemText>
                    </ListItem>
                    <AssetUploadingModeSelect />
                </Menu>
            </Paper>
        );
    }

    componentDidMount() {
        document.addEventListener('keydown', this.onDocumentKeyDown, false);
    };

    componentWillUnmount() {
        const { setPlayingPreview } = this.props;

        document.removeEventListener('keydown', this.onDocumentKeyDown);
        setPlayingPreview(false);
    };

    onDocumentKeyDown = (event) => {
        detectLocalPausePlayPreviewPress(event, () => {
            // Remove focus from different controls to prevent the native behavior
            if (document.activeElement && !['TEXTAREA', 'INPUT'].includes(document.activeElement.tagName))
                document.activeElement.blur();

            event.preventDefault();
            event.stopPropagation();
            this.props.setPlayingPreview(!this.props.playingPreview);
        });
    }

    onHelpButtonClick = () => {
        const { setPlayingPreview, setProperty } = this.props;

        setProperty('currentSettingsPanel', CreatePageConstants.SETTINGS_PANELS.GENERAL);
        setProperty('showWalkthrough', true);
        setPlayingPreview(false);

        trackEvent('Opened Walkthrough', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
        });
    };
}

const mapStateToProps = (state) => {
    const { playingPreview, optimizationModeEnabled } = state.create;
    return { playingPreview, optimizationModeEnabled }
};

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setPlayingPreview = (playingPreview) => dispatch(createActions.setPlayingPreview(playingPreview));

    return {
        setProperty,
        setPlayingPreview
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BottomBarActions, styles));